import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import {
  ArrowLeft,
  GitCompare,
  RefreshCw,
  Users,
  Activity,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Calendar,
  ArrowUpRight,
  ArrowDownLeft,
  Link2,
  Equal,
  Clock
} from 'lucide-react';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const ESTADO_CONFIG = {
  en_proceso: { label: 'En Proceso', color: 'bg-blue-100 text-blue-700' },
  completada: { label: 'Completada', color: 'bg-green-100 text-green-700' },
  archivada: { label: 'Archivada', color: 'bg-slate-100 text-slate-700' }
};

const InvestigacionComparar = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [investigations, setInvestigations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [selectedId1, setSelectedId1] = useState(searchParams.get('id1') || '');
  const [selectedId2, setSelectedId2] = useState(searchParams.get('id2') || '');
  const [comparisonData, setComparisonData] = useState(null);

  useEffect(() => {
    loadInvestigations();
  }, []);

  useEffect(() => {
    if (selectedId1 && selectedId2 && selectedId1 !== selectedId2) {
      loadComparison();
    }
  }, [selectedId1, selectedId2]);

  const loadInvestigations = async () => {
    setLoading(true);
    try {
      const res = await api.get('/investigations', { params: { limit: 100 } });
      setInvestigations(res.data.investigations || []);
    } catch (error) {
      toast.error('Error al cargar investigaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadComparison = async () => {
    if (!selectedId1 || !selectedId2) return;
    
    setComparing(true);
    try {
      const res = await api.get(`/investigations/compare/${selectedId1}/${selectedId2}`);
      setComparisonData(res.data);
    } catch (error) {
      toast.error('Error al comparar investigaciones');
      console.error(error);
    } finally {
      setComparing(false);
    }
  };

  const inv1 = comparisonData?.investigacion_1;
  const inv2 = comparisonData?.investigacion_2;
  const comp = comparisonData?.comparacion;

  // Combinar evolución temporal para comparación
  const combinedEvolution = useMemo(() => {
    if (!inv1 || !inv2) return [];
    
    const ev1 = inv1.resultados?.analisis_individual?.[0]?.evolucion_temporal || [];
    const ev2 = inv2.resultados?.analisis_individual?.[0]?.evolucion_temporal || [];
    
    const allDates = new Set([
      ...ev1.map(e => e.fecha),
      ...ev2.map(e => e.fecha)
    ]);
    
    return Array.from(allDates).sort().map(fecha => {
      const d1 = ev1.find(e => e.fecha === fecha) || {};
      const d2 = ev2.find(e => e.fecha === fecha) || {};
      return {
        fecha: fecha.substring(5), // MM-DD
        vol_inv1: d1.volumen || 0,
        vol_inv2: d2.volumen || 0
      };
    });
  }, [inv1, inv2]);

  const renderDiffBadge = (diff, isVolume = false) => {
    if (diff === 0) {
      return <Badge className="bg-slate-100 text-slate-600"><Equal size={12} className="mr-1" /> Igual</Badge>;
    }
    if (diff > 0) {
      return (
        <Badge className="bg-green-100 text-green-700">
          <TrendingUp size={12} className="mr-1" />
          +{isVolume ? formatMoney(diff) : diff.toLocaleString()}
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-700">
        <TrendingDown size={12} className="mr-1" />
        {isVolume ? formatMoney(diff) : diff.toLocaleString()}
      </Badge>
    );
  };

  const InvestigationCard = ({ inv, label, color }) => {
    if (!inv) return null;
    const resultados = inv.resultados || {};
    const resumen = resultados.resumen_global || {};
    const estadoConfig = ESTADO_CONFIG[inv.estado] || ESTADO_CONFIG.en_proceso;
    
    return (
      <Card className={`p-4 bg-white border-t-4`} style={{ borderTopColor: color }}>
        <div className="flex items-center justify-between mb-3">
          <Badge style={{ backgroundColor: color, color: 'white' }}>{label}</Badge>
          <Badge className={estadoConfig.color}>{estadoConfig.label}</Badge>
        </div>
        
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          INV-{String(inv.numero_investigacion).padStart(4, '0')}
        </h3>
        <p className="text-sm text-slate-600 mb-3">{inv.titulo}</p>
        
        <div className="text-xs text-slate-500 mb-4 flex items-center gap-2">
          <Calendar size={12} />
          {inv.fecha_desde} - {inv.fecha_hasta}
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-slate-900">
              {resumen.total_clientes || 0}
            </p>
            <p className="text-xs text-slate-500">Clientes</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg text-center">
            <p className="text-2xl font-bold text-slate-900">
              {(resumen.total_transacciones || 0).toLocaleString()}
            </p>
            <p className="text-xs text-slate-500">Transacciones</p>
          </div>
          <div className="p-3 bg-purple-50 rounded-lg text-center col-span-2">
            <p className="text-2xl font-bold text-purple-600">
              {formatMoney(resumen.volumen_total)}
            </p>
            <p className="text-xs text-slate-500">Volumen Total</p>
          </div>
        </div>
        
        {/* Patrones detectados */}
        {resultados.analisis_individual?.[0]?.patrones_detectados?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs font-medium text-slate-600 mb-2">Patrones detectados:</p>
            <div className="flex flex-wrap gap-1">
              {resultados.analisis_individual[0].patrones_detectados.slice(0, 3).map((p) => (
                <Badge key={p.name} className={`text-xs ${
                  p.severity === 'high' ? 'bg-red-100 text-red-700' :
                  p.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {p.name}
                </Badge>
              ))}
              {resultados.analisis_individual[0].patrones_detectados.length > 3 && (
                <Badge className="bg-slate-100 text-slate-600 text-xs">
                  +{resultados.analisis_individual[0].patrones_detectados.length - 3}
                </Badge>
              )}
            </div>
          </div>
        )}
        
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-4"
          onClick={() => navigate(`/m/monitoreo-transaccional/investigaciones/${inv.id}`)}
        >
          Ver Detalle
        </Button>
      </Card>
    );
  };

  return (
    <div className="p-4 lg:p-6 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/m/monitoreo-transaccional/investigaciones')}
          >
            <ArrowLeft size={16} className="mr-1" />
            Volver
          </Button>
        </div>
        
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <GitCompare className="text-purple-600" />
          Comparar Investigaciones
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Selecciona dos investigaciones para comparar sus resultados lado a lado
        </p>
      </div>

      {/* Selectores */}
      <Card className="p-4 bg-white mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">
              Investigación 1
            </label>
            <Select value={selectedId1} onValueChange={setSelectedId1}>
              <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="select-inv1">
                <SelectValue placeholder="Seleccionar investigación..." />
              </SelectTrigger>
              <SelectContent>
                {investigations.map((inv) => (
                  <SelectItem 
                    key={inv.id} 
                    value={inv.id}
                    disabled={inv.id === selectedId2}
                  >
                    INV-{String(inv.numero_investigacion).padStart(4, '0')} - {inv.titulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex justify-center">
            <div className="p-3 bg-slate-100 rounded-full">
              <GitCompare size={24} className="text-slate-400" />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-slate-700 block mb-2">
              Investigación 2
            </label>
            <Select value={selectedId2} onValueChange={setSelectedId2}>
              <SelectTrigger style={{ backgroundColor: '#fff' }} data-testid="select-inv2">
                <SelectValue placeholder="Seleccionar investigación..." />
              </SelectTrigger>
              <SelectContent>
                {investigations.map((inv) => (
                  <SelectItem 
                    key={inv.id} 
                    value={inv.id}
                    disabled={inv.id === selectedId1}
                  >
                    INV-{String(inv.numero_investigacion).padStart(4, '0')} - {inv.titulo}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Loading */}
      {comparing && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw size={32} className="animate-spin text-purple-600" />
        </div>
      )}

      {/* Resultados de comparación */}
      {comparisonData && !comparing && (
        <>
          {/* Métricas de comparación */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <Activity size={20} className="text-blue-600" />
                <span className="font-medium text-slate-700">Transacciones</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-slate-900">
                    {comp.transacciones.inv1.toLocaleString()}
                  </p>
                  <p className="text-xs text-purple-600">INV 1</p>
                </div>
                <div className="flex items-center justify-center">
                  {renderDiffBadge(comp.transacciones.diferencia)}
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900">
                    {comp.transacciones.inv2.toLocaleString()}
                  </p>
                  <p className="text-xs text-blue-600">INV 2</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 bg-white">
              <div className="flex items-center gap-3 mb-3">
                <DollarSign size={20} className="text-green-600" />
                <span className="font-medium text-slate-700">Volumen Total</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-purple-600">
                    {formatMoney(comp.volumen.inv1)}
                  </p>
                  <p className="text-xs text-purple-600">INV 1</p>
                </div>
                <div className="flex items-center justify-center">
                  {renderDiffBadge(comp.volumen.diferencia, true)}
                </div>
                <div>
                  <p className="text-lg font-bold text-blue-600">
                    {formatMoney(comp.volumen.inv2)}
                  </p>
                  <p className="text-xs text-blue-600">INV 2</p>
                </div>
              </div>
            </Card>

            <Card className={`p-4 ${comp.hay_overlap ? 'bg-amber-50 border-amber-200' : 'bg-white'}`}>
              <div className="flex items-center gap-3 mb-3">
                <Link2 size={20} className={comp.hay_overlap ? 'text-amber-600' : 'text-slate-400'} />
                <span className="font-medium text-slate-700">Clientes en Común</span>
              </div>
              {comp.hay_overlap ? (
                <div>
                  <p className="text-2xl font-bold text-amber-600 mb-2">
                    {comp.clientes_comunes.length}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {comp.clientes_comunes.slice(0, 2).map((c) => (
                      <Badge key={c} className="bg-amber-100 text-amber-700 text-xs font-mono">
                        {c.length > 12 ? `${c.substring(0, 8)}...` : c}
                      </Badge>
                    ))}
                    {comp.clientes_comunes.length > 2 && (
                      <Badge className="bg-amber-100 text-amber-700 text-xs">
                        +{comp.clientes_comunes.length - 2}
                      </Badge>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-2">
                  <CheckCircle size={24} className="mx-auto text-green-500 mb-1" />
                  <p className="text-sm text-slate-500">Sin superposición</p>
                </div>
              )}
            </Card>
          </div>

          {/* Cards lado a lado */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <InvestigationCard inv={inv1} label="Investigación 1" color="#7c3aed" />
            <InvestigationCard inv={inv2} label="Investigación 2" color="#3b82f6" />
          </div>

          {/* Gráfico comparativo de evolución */}
          {combinedEvolution.length > 0 && (
            <Card className="p-4 bg-white">
              <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <TrendingUp size={16} />
                Comparación de Evolución Temporal
              </h3>
              <div style={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={combinedEvolution}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="fecha" tick={{ fontSize: 10 }} />
                    <YAxis 
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => `${(v/1000000).toFixed(1)}M`}
                    />
                    <Tooltip 
                      formatter={(v) => formatMoney(v)}
                      labelFormatter={(l) => `Fecha: ${l}`}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="vol_inv1" 
                      stroke="#7c3aed" 
                      fill="#7c3aed" 
                      fillOpacity={0.3}
                      name="Investigación 1"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="vol_inv2" 
                      stroke="#3b82f6" 
                      fill="#3b82f6" 
                      fillOpacity={0.3}
                      name="Investigación 2"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}
        </>
      )}

      {/* Estado vacío */}
      {!comparisonData && !comparing && !loading && (
        <Card className="p-12 bg-white text-center">
          <GitCompare size={48} className="mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-600 mb-2">
            Selecciona dos investigaciones
          </h3>
          <p className="text-sm text-slate-400">
            Elige investigaciones diferentes para ver la comparación lado a lado
          </p>
        </Card>
      )}
    </div>
  );
};

export default InvestigacionComparar;
